/**
 * Shared team-panel request, resource lifecycle, layout, and form primitives. UI admin flags are
 * presentation context; authorization remains on the team API.
 *
 * @module apps/web/src/components/teams/shared
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { TeamWorkspaceData } from '@fluxability/types';
import { request } from '@/lib/api';
import { readUiCache, writeUiCache } from '@/lib/uiCache';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/** UI-cache key of a team resource path. */
const resourceKey = (path: string) => `team-resource:${path}`;

/**
 * Call the shared credentialed API client, return the successful data payload, and throw the API
 * error message for failed response envelopes. `T` names the payload shape the caller reads; it
 * is not validated, and a 204 response resolves to `undefined`.
 */
export async function teamRequest<T = unknown>(
  path: string,
  method = 'GET',
  payload?: unknown
): Promise<T | undefined> {
  const response = await request<T>(path, {
    method,
    ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
  });
  if (!response.success)
    throw new Error(response.error?.message || 'The request could not be completed.');
  return response.data;
}

/**
 * Load a resource when its path or reload revision changes. A path loaded before in this session
 * shows its last payload at once (with `loading` false) while the request refreshes it; an unseen
 * path starts empty and loading. A failed load clears the payload and reports the error. Ignores
 * completions from an obsolete effect; does not cancel the underlying fetch.
 */
export function useTeamResource<T = unknown>(path: string) {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    /** Last loaded payload, or null while loading or after a failure. */ data: T | null;
    /** Failure message of the last load, or ''. */ error: string;
    /** Whether a load is in flight and there is no earlier payload to show. */ loading: boolean;
  }>(() => {
    const cached = readUiCache<T | null>(resourceKey(path));
    return cached === undefined
      ? { data: null, error: '', loading: true }
      : { data: cached, error: '', loading: false };
  });
  const reload = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    let current = true;
    const cached = readUiCache<T | null>(resourceKey(path));
    setState(previous =>
      cached !== undefined
        ? { data: cached, error: '', loading: false }
        : previous.loading && previous.data === null && !previous.error
          ? previous
          : { data: null, error: '', loading: true }
    );
    teamRequest<T>(path).then(
      data => {
        // A 204 resolves to undefined; the resource stores null for "no data" either way.
        writeUiCache(resourceKey(path), data ?? null);
        if (current) setState({ data: data ?? null, error: '', loading: false });
      },
      error => {
        writeUiCache(resourceKey(path), undefined);
        if (current) setState({ data: null, error: error.message, loading: false });
      }
    );
    return () => {
      current = false;
    };
  }, [path, revision]);
  return { ...state, reload };
}

/**
 * Card wrapper with a required title, optional description, and content children for team screens.
 */
export function Panel({
  title,
  description,
  children,
}: {
  /** Visible heading for this panel or collection. */
  title: string;
  /** Optional explanatory text beneath the panel heading. */
  description?: string;
  /** Controls or content supplied by the owning view. */
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}
/**
 * Render a resource's loading status or error with retry; render nothing once it is ready.
 */
export function ResourceState({ resource }: { /** Loading/data/error state and retry callback returned by useTeamResource. */
resource: ReturnType<typeof useTeamResource> }) {
  return resource.loading ? (
    <p role="status">Loading…</p>
  ) : resource.error ? (
    <div role="alert">
      <p>{resource.error}</p>
      <Button variant="outline" onClick={resource.reload}>
        Try again
      </Button>
    </div>
  ) : null;
}
/**
 * Wrap a form control in a visible label with shared team-field spacing.
 */
export function Field({ label, children }: { /** Visible caption describing this control or metric. */
label: string; /** Controls or content supplied by the owning view. */
children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5 text-sm font-medium">
      {label}
      {children}
    </label>
  );
}
/**
 * Shared classes for compact team form inputs.
 */
export const inputClass = 'h-10 min-w-0 rounded-md border bg-background px-3 text-sm';
/**
 * Render a non-empty operation message in a live status paragraph.
 */
export function Status({ message }: { /** Operation feedback; an empty string renders no status paragraph. */
message: string }) {
  return message ? (
    <p role="status" className="text-sm">
      {message}
    </p>
  ) : null;
}
/**
 * Build a team-scoped API path, URL-encoding the team ID while preserving the caller's endpoint
 * suffix.
 */
export const teamPath = (id: string, suffix: string) =>
  `/teams/${encodeURIComponent(id)}/${suffix}`;
/**
 * Shared team-panel context: selected team ID, loaded workspace, and an admin UI flag. The flag does not replace API authorization.
 */
export type TeamPanelProps = { /** Selected team ID used to scope API requests; the server validates membership and permission. */
teamId: string; /** Loaded team workspace containing the members, bundles, fields, and capabilities used by this panel. */
workspace: TeamWorkspaceData; /** Server-derived UI capability hint for displaying admin controls; every API operation still checks permission. */
isAdmin: boolean };
